import { z } from "zod";
import type { Band, RuleSetMeta } from "../types.js";

/** AU-08 — Private Health LHC + MLS calculator. */

export const Au08InputSchema = z.object({
  /** Age in completed years on the assessment date. */
  age: z.number().int().min(16).max(120),
  /** Single vs family determines which MLS threshold table applies. */
  filingStatus: z.enum(["single", "family"]),
  /**
   * The user's own income for MLS purposes (taxable income + reportable
   * fringe benefits + reportable super + net investment loss). The MLS,
   * if any, is levied on THIS figure regardless of filing status.
   */
  income: z.number().min(0),
  /**
   * Partner's income for MLS purposes. Combined with `income` it sets the
   * threshold *tier* for family filers. Ignored when filing single.
   */
  partnerIncome: z.number().min(0).default(0),
  /** Dependent children — shifts the family threshold up by $1,500 each
   * after the first. */
  children: z.number().int().min(0).default(0),
  /** Whether the user currently holds an eligible private hospital policy. */
  holdsHospitalCover: z.boolean(),
  /**
   * Whether the user's spouse/partner currently holds an eligible private
   * hospital policy. Only meaningful for `filingStatus: "family"`; ignored
   * when filing single. MLS is assessed per individual — to avoid the
   * surcharge the ATO requires an appropriate hospital policy covering the
   * person, their spouse AND all dependants, so a partner who lacks cover
   * leaves the household exposed even if the user is personally covered.
   * Defaults to `false` (unknown / not covered) so we never understate
   * household exposure.
   */
  partnerHoldsHospitalCover: z.boolean().default(false),
  /**
   * Calendar year the user first held continuous hospital cover. `null`
   * when they hold no cover, or `"unsure"` when they hold cover but can't
   * recall — the latter yields an indeterminate LHC result.
   */
  firstHeldYear: z.union([z.number().int(), z.literal("unsure")]).nullable(),
});

export type Au08Input = z.infer<typeof Au08InputSchema>;

/**
 * Household hospital-cover picture for a family/couple assessment. MLS is a
 * per-individual liability, but the ATO only exempts a person when an
 * appropriate policy covers them, their spouse AND all dependants — so the
 * household can be exposed through whichever member lacks cover. We surface
 * the whole picture for information-only copy; the engine never recommends
 * adding anyone to a policy.
 *  - "single"        — single filer; partner cover not assessed.
 *  - "all_covered"   — user and partner both hold eligible cover.
 *  - "user_only"     — user covered, partner not (household exposed via partner).
 *  - "partner_only"  — partner covered, user not (household exposed via user).
 *  - "none_covered"  — neither holds cover.
 */
export type SpouseCoverSituation =
  | "single"
  | "all_covered"
  | "user_only"
  | "partner_only"
  | "none_covered";

export interface MlsResult {
  /** Surcharge payable for the FY, in whole dollars (rounded). */
  amount: number;
  /** Applicable surcharge rate as a fraction (0, 0.01, 0.0125, 0.015). */
  rate: number;
  /** Income tested to pick the rate tier (own income, or combined for
   * family). */
  testedIncome: number;
  /** Income the surcharge was levied on (always the user's own income). */
  leviedIncome: number;
  /** True when no surcharge applies because the user holds hospital cover. */
  exemptViaCover: boolean;
  /** Household cover picture (drives family copy). `"single"` for single
   * filers. */
  spouseCover: SpouseCoverSituation;
  /**
   * True when the household has MLS exposure through a member *other than*
   * the user — i.e. the user personally holds cover (so pays nothing) but
   * their partner does not. The user's own `amount` is still 0; this flags
   * that the family unit is not fully covered. Always false for single
   * filers and when everyone is covered.
   */
  householdExposedViaPartner: boolean;
}

export type LhcStatus =
  | "not_yet_applicable" // user is 30 or under — base day hasn't passed
  | "no_loading" // held cover by base day, or loading has dropped off
  | "would_apply_if_taken_now" // no cover; this is the loading they'd incur
  | "applies" // holds cover taken late; loading currently on their premium
  | "indeterminate"; // holds cover but first-held year unknown

export interface LhcResult {
  status: LhcStatus;
  /** Loading as a fraction of base premium (0 to 0.70). */
  loading: number;
  /** Years of loading remaining before it drops off (only when `applies`). */
  yearsRemaining: number | null;
}

export interface Au08Output {
  mls: MlsResult;
  lhc: LhcResult;
}

export interface Au08RuleSet extends RuleSetMeta {
  /** Calendar year used as "now" for LHC age/base-day math within this FY. */
  assessmentYear: number;
  mls: {
    single: readonly Band[];
    family: readonly Band[];
    /** Added to every family threshold boundary per child after the first. */
    familyChildIncrement: number;
  };
  lhc: {
    /** Age by which cover must be held to avoid loading (1 July after 31st
     * birthday → treat ages up to this as grace). */
    graceAge: number;
    /** Loading added per year of age over 30 when cover first taken. */
    loadingPerYear: number;
    /** Maximum loading. */
    cap: number;
    /** Continuous years of cover after which loading drops off. */
    dropoffYears: number;
  };
}
